import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Autocomplete,
  Marker,
  Circle,
  Polygon,
} from "@react-google-maps/api";
import { InputAdornment, TextField } from "@mui/material";
import config from "../../config.js";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import { GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsLibraries";

const containerStyle = {
  height: "100%",
  width: "100%",
  borderRadius: "20px",
  border: "1px solid #000000",
};

const inputStyles = {
  position: "absolute",
  top: "20px",
  left: "20px",
  zIndex: 1,
};

const defaultCenter = {
  lat: 13.198909,
  lng: 77.706893,
};

const toLatLng = (latitude, longitude) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

// Only load the libraries we need
const libraries = GOOGLE_MAPS_LIBRARIES;

export default function StopAddMap({
  initialLatitude,
  initialLongitude,
  onPositionChange,
  sitePreview = null,
}) {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { settings } = useSettings();
  const { getIntermediateStopIcon } = useMapIcons(settings, [], [], []);

  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [marker, setMarker] = useState(null);
  const autoCompleteRef = useRef(null);
  const containerRef = useRef(null);

  const getSiteCenter = (site) => {
    if (!site?.sitCoordinates?.length) return null;
    const coords = [...site.sitCoordinates].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const points = coords
      .map((c) => ({
        lat: parseFloat(c.latitude),
        lng: parseFloat(c.longitude),
      }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
    if (!points.length) return null;
    if ((site.boundaryType || "").toUpperCase() === "CUSTOM" && points.length > 1) {
      const avgLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
      const avgLng = points.reduce((s, p) => s + p.lng, 0) / points.length;
      return { lat: avgLat, lng: avgLng };
    }
    return points[0];
  };

  useEffect(() => {
    const nextPosition = toLatLng(initialLatitude, initialLongitude);
    if (nextPosition) {
      setCenter(nextPosition);
      setMarker(nextPosition);
      if (map) {
        map.panTo(nextPosition);
        map.setZoom(15);
      }
    } else {
      // Keep current map viewport (site preview), but clear selected stop marker.
      setMarker(null);
    }
  }, [initialLatitude, initialLongitude, map]);

  useEffect(() => {
    if (!map || !sitePreview?.sitCoordinates?.length) return;
    const type = (sitePreview.boundaryType || "").toUpperCase();
    const coords = [...sitePreview.sitCoordinates].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const points = coords
      .map((c) => ({ lat: parseFloat(c.latitude), lng: parseFloat(c.longitude) }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
    if (!points.length) return;

    if (type === "CUSTOM" && points.length >= 3 && window.google?.maps?.LatLngBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
      return;
    }

    const centerPoint = getSiteCenter(sitePreview) || points[0];
    map.panTo(centerPoint);
    const radiusKm = parseFloat(sitePreview.radius || 0);
    if (radiusKm > 0) {
      if (radiusKm > 5) map.setZoom(12);
      else if (radiusKm > 2) map.setZoom(13);
      else if (radiusKm > 1) map.setZoom(14);
      else map.setZoom(15);
    } else {
      map.setZoom(15);
    }
  }, [map, sitePreview]);

  useEffect(() => {
    if (!map || !containerRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      window.google?.maps?.event.trigger(map, "resize");
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [map]);

  const onLoad = React.useCallback(
    function callback(map) {
      const initialPosition = toLatLng(initialLatitude, initialLongitude);
      if (initialPosition) {
        map.panTo(initialPosition);
      } else {
        // Only set bounds if we don't have initial coordinates
        const bounds = new window.google.maps.LatLngBounds(center);
        map.fitBounds(bounds);
      }
      setMap(map);
    },
    [center, initialLatitude, initialLongitude],
  );

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);

  const handlePlaceSelect = () => {
    try {
      if (autoCompleteRef.current) {
        const place = autoCompleteRef.current.getPlace();
        if (place && place.geometry && place.geometry.location) {
          const newPosition = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          updatePosition(newPosition);
        } else {
          console.warn("Selected place doesn't have valid geometry");
        }
      }
    } catch (error) {
      console.error("Error selecting place:", error);
    }
  };

  const handleMapClick = (event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    updatePosition(newPosition);
  };

  const updatePosition = (position) => {
    setCenter(position);
    setMarker(position);
    if (map) {
      map.panTo(position);
      map.setZoom(15);
    }
    onPositionChange(position.lat, position.lng);
  };

  if (loadError) return <div>Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
    >
      <div style={inputStyles}>
        <Autocomplete
          onLoad={(autoComplete) => {
            autoCompleteRef.current = autoComplete;
          }}
          onPlaceChanged={handlePlaceSelect}
        >
          <div
            className="search-filter-wrapper search_container"
            style={{
              position: "absolute",
              zIndex: 1000,
              backgroundColor: "transparent",
            }}
          >
            <TextField
              size="small"
              id="search"
              sx={{ backgroundColor: "white" }}
              label={t("search")}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#000000"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-search"
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                style: {
                  border: "none",
                },
              }}
            />
          </div>
        </Autocomplete>
      </div>

      {marker && Number.isFinite(marker.lat) && Number.isFinite(marker.lng) ? (
        <Marker
          position={marker}
          icon={getIntermediateStopIcon() || undefined}
        />
      ) : null}

      {sitePreview?.sitCoordinates?.length ? (
        (sitePreview.boundaryType || "").toUpperCase() === "CUSTOM" ? (
          <Polygon
            paths={[...sitePreview.sitCoordinates]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((c) => ({
                lat: parseFloat(c.latitude),
                lng: parseFloat(c.longitude),
              }))
              .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng))}
            options={{
              fillColor: "#20A8E0",
              fillOpacity: 0.15,
              strokeColor: "#20A8E0",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              clickable: false,
              editable: false,
            }}
          />
        ) : (
          (() => {
            const centerPoint = getSiteCenter(sitePreview);
            if (!centerPoint) return null;
            return (
              <Circle
                center={centerPoint}
                radius={Math.max(0, parseFloat(sitePreview.radius || 0)) * 1000}
                options={{
                  fillColor: "#20A8E0",
                  fillOpacity: 0.15,
                  strokeColor: "#20A8E0",
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  clickable: false,
                  editable: false,
                }}
              />
            );
          })()
        )
      ) : null}
    </GoogleMap>
    </div>
  );
}
